import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { ResourceType } from '@common/contracts';
import ImageDropzone from '../ImageDropzone/ImageDropzone';
import { FaChevronDown } from 'react-icons/fa';
// Predefined list of technologies
const AVAILABLE_TECHNOLOGIES = [
    'Empathy',
    'Grid',
    'Vision',
    'Network',
    'Entropy',
    'Resonance',
    'Predictor',
    'Temporal'
];
export function ResourceModalForm({ isOpen, onClose, onSubmit, isLoading, mode, resource, title, submitIdleLabel, submitBusyLabel }) {
    const [name, setName] = useState('');
    const [technology, setTechnology] = useState('');
    const [image, setImage] = useState(null);
    const [type, setType] = useState(ResourceType.Resource);
    const [nameError, setNameError] = useState('');
    const [technologyError, setTechnologyError] = useState('');
    const [isTechDropdownOpen, setIsTechDropdownOpen] = useState(false);
    const techSelectRef = useRef(null);
    // Reset (create) or populate (edit) the form state every time the modal opens
    useEffect(() => {
        if (!isOpen)
            return;
        if (mode === 'edit') {
            if (!resource)
                return;
            setName(resource.name);
            setTechnology(resource.technology);
            setImage(resource.image);
            setType(resource.type);
        }
        else {
            setName('');
            setTechnology('');
            setImage(null);
            setType(ResourceType.Resource);
        }
        setNameError('');
        setTechnologyError('');
        setIsTechDropdownOpen(false);
    }, [isOpen, resource, mode]);
    const handleSubmit = (e) => {
        e.preventDefault();
        let hasErrors = false;
        // Name validation
        if (!name.trim()) {
            setNameError('Name is required');
            hasErrors = true;
        }
        // Technology validation
        if (!technology.trim()) {
            setTechnologyError('Technology is required');
            hasErrors = true;
        }
        if (hasErrors)
            return;
        onSubmit({
            name: name.trim(),
            technology: technology.trim(),
            type,
            image
        });
    };
    const handleClose = () => {
        setName('');
        setTechnology('');
        setImage(null);
        setType(ResourceType.Resource);
        setNameError('');
        setTechnologyError('');
        setIsTechDropdownOpen(false);
        onClose();
    };
    const handleNameChange = (e) => {
        const value = e.target.value;
        setName(value);
        if (nameError && value.trim()) {
            setNameError('');
        }
    };
    const handleTechnologyChange = (value) => {
        setTechnology(value);
        setIsTechDropdownOpen(false);
        if (technologyError && value.trim()) {
            setTechnologyError('');
        }
    };
    const toggleTechDropdown = () => {
        if (!isLoading) {
            setIsTechDropdownOpen(!isTechDropdownOpen);
        }
    };
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (techSelectRef.current && !techSelectRef.current.contains(event.target)) {
                setIsTechDropdownOpen(false);
            }
        };
        if (isTechDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isTechDropdownOpen]);
    if (!isOpen || (mode === 'edit' && !resource))
        return null;
    return (_jsx("div", { className: "modal-overlay", onClick: handleClose, children: _jsxs("div", { className: "modal-content", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "modal-header", children: [_jsx("h2", { children: title }), _jsx("button", { className: "close-button", onClick: handleClose, disabled: isLoading, children: "\u00D7" })] }), _jsxs("form", { onSubmit: handleSubmit, className: "modal-form", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "resourceName", children: "Name" }), _jsx("input", { id: "resourceName", type: "text", value: name, onChange: handleNameChange, placeholder: "Enter resource name", disabled: isLoading, className: nameError ? 'error' : '' }), nameError && _jsx("span", { className: "error-text", children: nameError })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "resourceTechnology", children: "Technology" }), _jsxs("div", { className: `technology-select ${isTechDropdownOpen ? 'open' : ''} ${technologyError ? 'error' : ''}`, ref: techSelectRef, children: [_jsxs("div", { className: "technology-select-display", onClick: toggleTechDropdown, children: [_jsx("span", { className: technology ? 'selected' : 'placeholder', children: technology || 'Select technology...' }), _jsx(FaChevronDown, { className: "dropdown-arrow" })] }), isTechDropdownOpen && (_jsx("ul", { className: "technology-select-options", children: AVAILABLE_TECHNOLOGIES.map(tech => (_jsx("li", { className: `technology-option ${tech === technology ? 'selected' : ''}`, onClick: () => handleTechnologyChange(tech), children: tech }, tech))) }))] }), technologyError && _jsx("span", { className: "error-text", children: technologyError })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Type" }), _jsx("button", { type: "button", className: `type-toggle-button ${type === ResourceType.Artefact ? 'artefact' : ''}`, onClick: () => setType(type === ResourceType.Resource ? ResourceType.Artefact : ResourceType.Resource), disabled: isLoading, children: type })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Image" }), _jsx(ImageDropzone, { currentImageUrl: image, onUploadSuccess: setImage })] }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", onClick: handleClose, disabled: isLoading, children: "Cancel" }), _jsx("button", { type: "submit", disabled: isLoading || !name.trim() || !technology.trim(), children: isLoading ? submitBusyLabel : submitIdleLabel })] })] })] }) }));
}
